import { ProFormSelect, ProFormSwitch, ProFormText } from '@ant-design/pro-components';
import { Form } from 'antd';
import PropTypes from 'prop-types';
import { useEffect } from 'react';

import {
  attributionTypeOptions,
  getMetricAllocationTypeOptions,
  getMetricPeriodTypeOptions,
  metricTypeOptions,
  sourceDomainValueEnum,
  statisticalModelTypeOptions,
} from './metric-options';
import { getMetricLifecycleValidationError } from '../utils/metric-lifecycle';

const valueTypeOptions = [
  { label: '金额', value: 'amount' },
  { label: '数量', value: 'quantity' },
  { label: '比率', value: 'ratio' },
];

const sourceDomainOptions = Object.entries(sourceDomainValueEnum).map(([value, { text }]) => ({
  label: text,
  value,
}));

const MetricAllocationTypeField = ({ metricType, sourceDomain }) => {
  const form = Form.useFormInstance();
  const periodType = Form.useWatch('periodType', form);
  const valueType = Form.useWatch('valueType', form);
  const required = valueType !== 'ratio';
  const options = getMetricAllocationTypeOptions(sourceDomain, { periodType, valueType });
  return metricType === 'atomic' ? (
    <ProFormSelect
      name="allocationType"
      label="生效策略"
      options={options}
      hidden={options.length === 0}
      rules={[
        ...(required ? [{ required: true, message: '请选择生效策略' }] : []),
        {
          validator: async (_, allocationType) => {
            const error = getMetricLifecycleValidationError({
              allocationType,
              metricType,
              periodType: form.getFieldValue('periodType'),
              sourceDomain,
              statisticalModelType: form.getFieldValue('statisticalModelType'),
              valueType,
            });
            if (error) {
              throw new Error(error);
            }
          },
        },
      ]}
      fieldProps={{
        allowClear: !required,
        placeholder: required ? '请选择生效策略' : '按指标周期生效',
      }}
      extra={
        valueType === 'ratio'
          ? '月周期可选择指定周期或相邻月份生效；留空按自然周期生效'
          : '日周期支持事件日生效；月周期可选择指定周期、相邻月份或租期次月生效，按来源领域限制可选策略'
      }
    />
  ) : null;
};

MetricAllocationTypeField.propTypes = {
  metricType: PropTypes.oneOf(['atomic', 'composite']),
  sourceDomain: PropTypes.string,
};

const MetricPeriodFields = ({ required = false, sourceDomain, valueType }) => {
  const form = Form.useFormInstance();
  const periodType = Form.useWatch('periodType', form);

  return (
    <>
      <ProFormSelect
        name="periodType"
        label="指标周期"
        options={getMetricPeriodTypeOptions({ sourceDomain, valueType })}
        rules={[
          ...(required ? [{ required: true, message: '请选择指标周期' }] : []),
          {
            validator: async (_, periodTypeValue) => {
              const error = getMetricLifecycleValidationError({
                allocationType: form.getFieldValue('allocationType'),
                metricType: 'atomic',
                periodType: periodTypeValue,
                sourceDomain,
                statisticalModelType: form.getFieldValue('statisticalModelType'),
                valueType,
              });
              if (error) {
                throw new Error(error);
              }
            },
          },
        ]}
        fieldProps={{
          allowClear: !required,
          placeholder: required ? '请选择指标周期' : '不规则',
        }}
        extra="不规则按事实期间分配；月周期先按月等额，再按生效期间分配到日；比率原值透传"
      />
      {periodType ? (
        <ProFormSelect
          name="statisticalModelType"
          label="推测模型"
          options={statisticalModelTypeOptions}
          fieldProps={{ allowClear: true, placeholder: '不启用' }}
        />
      ) : null}
    </>
  );
};

MetricPeriodFields.propTypes = {
  required: PropTypes.bool,
  sourceDomain: PropTypes.oneOf(['expense', 'income', 'plan', 'salary']),
  valueType: PropTypes.oneOf(['amount', 'quantity', 'ratio']),
};

const MetricAttributionTypeField = ({ metricType, sourceDomain }) => {
  const form = Form.useFormInstance();
  const attributionType = Form.useWatch('attributionType', form);
  const valueType = Form.useWatch('valueType', form);

  useEffect(() => {
    if (metricType === 'atomic' && !form.getFieldValue('attributionType')) {
      form.setFieldValue('attributionType', 'direct');
    }
  }, [attributionType, form, metricType]);

  if (metricType !== 'atomic') {
    return null;
  }
  const editable = ['expense', 'salary'].includes(sourceDomain) && valueType === 'amount';
  return (
    <ProFormSelect
      name="attributionType"
      label="归属策略"
      options={attributionTypeOptions}
      rules={[{ required: true, message: '请选择归属策略' }]}
      fieldProps={{ disabled: !editable }}
      extra={
        editable
          ? '总部费用及编码 11111 的费用、工资按生效期间分摊至北京门店，其他来源仍直接归属'
          : '仅经营费用、门店工资的金额指标支持总部管理费用分摊'
      }
    />
  );
};

MetricAttributionTypeField.propTypes = {
  metricType: PropTypes.oneOf(['atomic', 'composite']),
  sourceDomain: PropTypes.string,
};

const MetricFormFields = ({
  fixedMetricType,
  lockTypes = false,
  mappingComboCount = 0,
  sourceDomain,
}) => {
  const form = Form.useFormInstance();
  const metricType = Form.useWatch('metricType', form) || fixedMetricType;
  const valueType = Form.useWatch('valueType', form);

  return (
    <>
      <ProFormText
        name="name"
        label="指标名称"
        placeholder="请输入指标名称"
        rules={[{ required: true, message: '请输入指标名称' }]}
      />
      {!fixedMetricType && (
        <ProFormSelect
          name="metricType"
          label="指标类型"
          options={metricTypeOptions}
          rules={[{ required: true, message: '请选择指标类型' }]}
          fieldProps={{ disabled: lockTypes }}
        />
      )}
      {metricType === 'atomic' && (
        <ProFormSelect
          name="sourceDomain"
          label="领域类型"
          options={sourceDomainOptions}
          fieldProps={{ disabled: true }}
          placeholder={Number(mappingComboCount || 0) > 0 ? '配置异常' : '完成首次来源映射后确定'}
        />
      )}
      {metricType === 'atomic' ? (
        <MetricAllocationTypeField metricType={metricType} sourceDomain={sourceDomain} />
      ) : null}
      <MetricAttributionTypeField metricType={metricType} sourceDomain={sourceDomain} />
      <ProFormSelect
        name="valueType"
        label="值类型"
        options={valueTypeOptions}
        rules={[{ required: true, message: '请选择值类型' }]}
        fieldProps={{ disabled: lockTypes }}
      />
      {metricType === 'atomic' ? (
        <MetricPeriodFields
          required={valueType === 'ratio'}
          sourceDomain={sourceDomain}
          valueType={valueType}
        />
      ) : null}
      <ProFormSwitch name="enabled" label="是否启用" />
    </>
  );
};

MetricFormFields.propTypes = {
  fixedMetricType: PropTypes.oneOf(['atomic', 'composite']),
  lockTypes: PropTypes.bool,
  mappingComboCount: PropTypes.number,
  sourceDomain: PropTypes.oneOf(['expense', 'income', 'plan', 'salary']),
};

export default MetricFormFields;
